<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import GbBreadcrumbs, { type BreadcrumbItem } from '@/components/GbBreadcrumbs.vue'
import NoAccessError from '@/components/NoAccessError.vue'
import PageHeader from '@/components/PageHeader.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import SaveToLibraryPanel from './SaveToLibraryPanel.vue'
import PresentLevelsStageBreadcrumbs from './PresentLevelsStageBreadcrumbs.vue'
import { usePresentLevelsWizardStore } from './present_levels_wizard.store'
import { useAiProfileRedirect } from './useAiProfileRedirect'
import { WIZARD_EVENT_NAMES } from './event_names'
import { getGradeLabelForGradeLevel } from '@/presentlevels/api_v1.js'
import { useLibraryStore } from '@/library/library.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { toolkitUrls } from '@/util/links.js'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'

const CONCLUSION_ROUTE_PREFIX = 'PresentlevelsConclusion'

const route = useRoute()
const wizardStore = usePresentLevelsWizardStore()
const {
  breadcrumbsInfo,
  studentName,
  enrolledGrade,
  areasOfNeed,
  hasAccess,
  isSaving,
  lastUpdate,
  isBlockerShown,
  blockerPermissions
} = storeToRefs(wizardStore)
const libraryStore = useLibraryStore()
const { currentFolder, currentFolderUuid, canEditCurrentFolderContent } = storeToRefs(libraryStore)
const { effectsVisibility } = storeToRefs(useVisibilityStore())

const presentLevelUuid = computed(() => breadcrumbsInfo.value.uuid)
const hasStatement = computed(() => Boolean(presentLevelUuid.value))
const folderName = computed(() => currentFolder.value.name ?? '')
const isSavedToFolder = computed(() => Boolean(folderName.value))
const isAccessDenied = computed(() => hasAccess.value === false)
const isConclusion = computed(() => String(route.name ?? '').startsWith(CONCLUSION_ROUTE_PREFIX))

const analytics = getAnalyticsTracker().getChildContext({
  plw_enrolled_grade: enrolledGrade,
  plw_areas_of_need: areasOfNeed,
  plw_is_library: isSavedToFolder,
  is_library: isSavedToFolder,
  student_uuid: currentFolderUuid
})

function canSeeStageBreadcrumbs() {
  if (isAccessDenied.value || !effectsVisibility.value) {
    return false
  }
  const isReadOnlyFolderContent = Boolean(currentFolderUuid.value) && !canEditCurrentFolderContent.value
  return !isReadOnlyFolderContent
}

const isStageBreadcrumbsShown = computed(() => hasStatement.value && canSeeStageBreadcrumbs())
const isLibraryBannerShown = computed(() => isSavedToFolder.value || isAccessDenied.value)

function buildLibraryCrumbs(): BreadcrumbItem[] {
  const allFolders = { label: 'Library: All Folders', to: { name: 'AllFolders' } }
  // Vue 2 hid the folder crumbs until the conclusion page had confirmed access
  if (hasAccess.value !== true) {
    return [allFolders]
  }
  const gradeLabel = getGradeLabelForGradeLevel(enrolledGrade.value)
  return [
    allFolders,
    { label: folderName.value, icon: 'folder', to: { name: 'LibraryFolder', params: { uuid: currentFolderUuid.value } } },
    { label: `Present Levels: ${studentName.value} (${gradeLabel})` }
  ]
}

const libraryCrumbs = computed(buildLibraryCrumbs)

// The stage breadcrumbs below carry the gray band when they show, so the library trail drops its own
const libraryBannerClass = computed(() => ({ '[&_ol]:bg-transparent': isStageBreadcrumbsShown.value }))

// One router-view serves both layouts: switching between two would remount the routed page
// whenever it sets or clears the statement uuid, and the page would load itself again.
const pageLayoutClass = computed(() => {
  if (hasStatement.value) {
    return 'min-[992px]:grid min-[992px]:grid-cols-12 min-[992px]:gap-x-[30px]'
  }
  return 'mx-auto md:w-10/12 lg:w-8/12'
})

const mainColumnClass = computed(() => {
  if (!hasStatement.value) {
    return ''
  }
  if (isConclusion.value) {
    return 'min-[992px]:col-span-12'
  }
  return 'min-[992px]:col-span-9 xl:col-span-8'
})

const isSavePanelShown = computed(() => hasStatement.value && !isConclusion.value)

// The save panel looks its folder up too, but it is not shown on the conclusion pages,
// whose library banner still needs the statement's folder.
async function showStatementFolder(uuid: string) {
  await libraryStore.fetchFoldersForContentByType(CONTENT_TYPES.PRESENTLEVEL)
  libraryStore.setCurrentFolderByUuid(libraryStore.folderUuidsByContentUuid[uuid] ?? '')
}

watch(presentLevelUuid, (uuid) => {
  if (uuid) {
    showStatementFolder(uuid)
  }
})

function hasEverythingForDraftedEvent() {
  return Boolean(enrolledGrade.value && folderName.value && currentFolderUuid.value)
}

const stopWatchingForDraft = watch(
  [enrolledGrade, folderName, currentFolderUuid],
  () => {
    if (!hasEverythingForDraftedEvent()) {
      return
    }
    analytics.sendEvent(WIZARD_EVENT_NAMES.VIEWED_DRAFTED, {
      user_folder_role: libraryStore.getUserRoleForFolder(currentFolderUuid.value)
    })
    stopWatchingForDraft()
  }
)

function sendBrowseAssessmentsClick() {
  analytics.sendEvent(WIZARD_EVENT_NAMES.CLICKED_BROWSE_ASSESSMENTS)
}

wizardStore.resetStudentInfo()
analytics.sendEvent(WIZARD_EVENT_NAMES.VIEWED, { ref: route.query.ref })
useAiProfileRedirect()

onBeforeUnmount(() => {
  libraryStore.setCurrentFolderByUuid('')
})
</script>

<template>
  <div class="container" :class="VUE2_CONTAINER_WIDTHS">
    <GbBreadcrumbs
      v-if="isLibraryBannerShown"
      data-testid="library-breadcrumbs"
      class="mb-7 [&_ol]:rounded-none [&_ol]:py-2.5"
      :class="libraryBannerClass"
      :items="libraryCrumbs"
    />
    <NoAccessError v-if="isAccessDenied" />
    <template v-else>
      <PageHeader
        v-if="!isSavedToFolder"
        title="Present Levels Wizard"
        subtitle="Use the wizard to identify specific areas of strengths or needs for an individual student."
      />
      <p v-if="!isSavedToFolder && !hasStatement" class="mx-auto pb-2.5 pt-5 text-display-sm md:w-10/12 lg:w-8/12">
        Still need to gather student data?
        <a class="text-blue-400 underline" :href="toolkitUrls.browseAssessments" @click="sendBrowseAssessmentsClick">
          Browse Assessments
        </a>
      </p>

      <PresentLevelsStageBreadcrumbs
        v-if="isStageBreadcrumbsShown"
        :uuid="presentLevelUuid"
        :available-stages="breadcrumbsInfo.availableStages"
      />
      <div :class="pageLayoutClass">
        <div :class="mainColumnClass">
          <router-view />
        </div>
        <SaveToLibraryPanel
          v-if="isSavePanelShown"
          class="min-[992px]:col-span-3 xl:col-span-4"
          :content-uuid="presentLevelUuid"
          :last-saved-at="lastUpdate?.toString()"
          :is-content-saving="isSaving"
        />
      </div>
    </template>

    <PremiumActionModal
      v-if="blockerPermissions"
      v-model:external-open="isBlockerShown"
      :action-permissions="blockerPermissions"
    />
  </div>
</template>
